<template>
    <div v-if="hasSearch">
        <h4 class="mb-2">What are you craving?</h4>

        <CategoryFilterBar :categories="uniqueCategories"
                           :selected-ids="selectedCategoryNames"
                           @toggle="toggleCategory" />

        <div v-if="loading">Loading restaurants...</div>

        <div v-else class="vendor-grid">
            <div v-for="vendor in filteredVendors"
                 :key="vendor.id"
                 :class="[
          'vendor-card',
          {
            'not-yet-open': !vendor.availability?.isOpenNow && vendor.availability?.nextOpenUtc,
            'fully-closed': !vendor.availability?.isOpenNow && !vendor.availability?.nextOpenUtc,
            closed: !vendor.availability?.isOpenNow && isFullyClosed(vendor.availability)
          }
        ]"
                 @click="vendorClicked(vendor)">
                <div class="cover-container">
                    <img v-if="vendor.coverUrl"
                         :src="vendor.coverUrl"
                         alt="Vendor Cover"
                         class="vendor-cover" />
                    <div v-else class="vendor-cover fallback-cover"></div>

                    <img v-if="vendor.logoUrl"
                         :src="vendor.logoUrl"
                         alt="Vendor Logo"
                         class="vendor-logo" />

                    <div class="vendor-overlay">
                        <h3>{{ vendor.name }}</h3>
                        <p v-if="vendor.deliveryFee !== null">
                            Delivery: &pound;{{ vendor.deliveryFee.toFixed(2) }}
                            <span v-if="vendor.deliveryFreeOrdersOver">
                                (Free over &pound;{{ vendor.deliveryFreeOrdersOver.toFixed(2) }})
                            </span>
                        </p>
                        <div v-if="googlePlaceDetailsMap[vendor.id]">
                            <small>
                                <i class="fab fa-google fa-lg me-2"></i>
                                <i class="fa fa-star" />
                                {{ googlePlaceDetailsMap[vendor.id].rating }}
                                ({{ googlePlaceDetailsMap[vendor.id].reviewCount }} reviews)
                            </small>
                        </div>
                        <small v-if="vendor.distanceFromCustomer">
                            {{ vendor.distanceFromCustomer.toFixed(1) }} miles away
                        </small>

                        <div v-if="vendor.availability?.isOpenNow">
                            <span class="badge bg-success">Open Now</span>
                        </div>
                        <div v-else>
                            <span class="badge bg-danger">Closed</span>
                            <small v-if="vendor.availability?.nextOpenUtc">
                                — Opens {{ new Date(vendor.availability.nextOpenUtc).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }}
                            </small>
                            <small v-if="vendor.availability?.closingReason">
                                — {{ vendor.availability.closingReason }}
                            </small>
                        </div>

                        <p v-if="vendor.foodHygieneRating > 0">
                            Food Hygiene Rating: {{ getHygieneLabel(vendor.foodHygieneRating) }}
                        </p>
                        <small v-if="vendor.restaurantCategoryName">
                            {{ vendor.restaurantCategoryName }}
                        </small>
                    </div>
                </div>
            </div>
        </div>

        <small class="text-muted d-block text-center mt-2">Ratings powered by Google</small>
    </div>
    <div v-else><p>Please select a location.</p></div>
</template>

<script setup>
    import { ref, onMounted, computed } from 'vue'
    import { useMedia } from '../../composables/useMedia'
    import CategoryFilterBar from '../Parts/CategoryFilterBar.vue'

    const props = defineProps({
        onSelected: Function
    })

    const vendors = ref([])

    const selectedCategoryNames = ref([])

    const uniqueCategories = computed(() => {
        const names = new Set()
        vendors.value.forEach(v => {
            if (v.restaurantCategoryName) names.add(v.restaurantCategoryName)
        })
        return Array.from(names).sort()
    })

    const filteredVendors = computed(() => {
        if (!selectedCategoryNames.value.length) return vendors.value
        return vendors.value.filter(v =>
            selectedCategoryNames.value.includes(v.restaurantCategoryName)
        )
    })

    const toggleCategory = (name) => {
        const i = selectedCategoryNames.value.indexOf(name)
        if (i === -1) selectedCategoryNames.value.push(name)
        else selectedCategoryNames.value.splice(i, 1)
    }

    const loading = ref(true)
    const hasSearch = ref(false)
    const googlePlaceDetailsMap = ref({})

    const getHygieneLabel = (value) => {
        const ratings = {
            1: 'Awaiting Inspection',
            2: 'Exempt',
            3: '0 - Urgent Improvement Necessary',
            4: '1 - Major Improvement Necessary',
            5: '2 - Improvement Necessary',
            6: '3 - Generally Satisfactory',
            7: '4 - Good',
            8: '5 - Very Good'
        }
        return ratings[value] || 'Not Set'
    }

    const isFullyClosed = (avail) => {
        if (!avail?.nextOpenUtc) return true
        const next = new Date(avail.nextOpenUtc)
        const now = new Date()
        const diffMs = next - now
        const diffHours = diffMs / (1000 * 60 * 60)
        return diffHours > 24
    }

    const vendorClicked = (vendor) => {
        props.onSelected({
            slug: vendor.slug
            //vId: vendor.id,
            //deliveryFee: vendor.deliveryFee ?? 0,
            //deliveryFreeOrdersOver: vendor.deliveryFreeOrdersOver ?? null,
        })
    }

    const loadGooglePlaceDetails = async () => {
        if (!vendors.value.length) return

        const vendorIds = vendors.value.map(v => v.id)
        const res = await fetch(`/PublicGooglePlace/batch-place-details-by-vendor`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(vendorIds)
        })

        if (res.ok) {
            const placeDetailsList = await res.json()
            googlePlaceDetailsMap.value = placeDetailsList.reduce((map, details) => {
                map[details.vendorId] = details
                return map
            }, {})
        }
    }

    onMounted(async () => {
        loading.value = true

        const location = JSON.parse(localStorage.getItem('customerLocation') || '{}')
        if (!location.lat || !location.lng) return

        hasSearch.value = true
        const methods = [location.mode === 'pickup' ? 'collection' : 'delivery']

        const res = await fetch('/publicvendor/search', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                lat: location.lat,
                lng: location.lng,
                methods,
                maxDistanceMiles: 500
            })
        })

        vendors.value = await res.json()

        const { fetchVendorLogo, fetchVendorCover } = useMedia()

        await Promise.all(
            vendors.value.map(async (vendor) => {
                try {
                    vendor.logoUrl = await fetchVendorLogo(vendor.id)
                    vendor.coverUrl = await fetchVendorCover(vendor.id)
                } catch (err) {
                    console.error('Error loading media for vendor', vendor.id, err)
                    vendor.logoUrl = null
                    vendor.coverUrl = null
                }
            })
        )

        await loadGooglePlaceDetails()
        loading.value = false
    })
</script>


<style scoped>
    .menu-container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 1rem;
    }

</style>
